import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DialogService, DynamicDialogConfig, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from 'src/app/shared/page-layout/page-hero.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { Company } from 'src/app/company/company.model';
import { CrmClient } from 'src/app/crm/crm.model';
import { CrmService } from 'src/app/crm/crm.service';
import { Location } from 'src/app/location/location.model';
import { LocationService } from 'src/app/location/location.service';
import { LazyLocationPickerDirective } from 'src/app/shared/location-picker/lazy-location-picker.directive';
import { WarehousingMasterData } from 'src/app/warehousing/warehousing.model';
import { WarehousingService } from 'src/app/warehousing/warehousing.service';
import { ContainerFacilityService } from '../container-facility.service';
import { ContainerTerminalYardService } from '../container-terminal-yard.service';
import { ContainerFacility } from '../models/container-facility.model';
import { ContainerYardArea, ContainerYardChargeRule, ContainerYardSlot } from '../models/container-terminal-yard.model';
import {
  PartyQuickCreateDialogComponent,
  PartyQuickCreateResult
} from 'src/app/shared/party-quick-create/party-quick-create-dialog.component';
import { WarehousingMasterDataFormComponent } from 'src/app/warehousing/billing-config/warehousing-master-data-form.component';
import { ProductsService } from 'src/app/order/product.service';
import { Products } from 'src/app/order/models/product.model';
import { ProductVariant } from 'src/app/order/models/product-variant.model';

type SetupMasterForm = 'facilities' | 'areas' | 'slots' | 'charge-rules';

@Component({
  selector: 'app-yard-setup-master-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    DynamicDialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    LazyLocationPickerDirective,
    ToastModule,
    ToggleSwitchModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent,
    PartyQuickCreateDialogComponent
  ],
  providers: [MessageService, DialogService],
  templateUrl: './yard-setup-master-form.component.html',
  styleUrl: './yard-setup-master.component.css'
})
export class YardSetupMasterFormComponent implements OnInit {
  master: SetupMasterForm = 'facilities';
  id: number | null = null;
  isLoading = false;
  isSubmitting = false;
  isDialogMode = false;
  form!: FormGroup;
  locationQuickCreateVisible = false;
  supplierQuickCreateVisible = false;
  supplierQuickCreateSaving = false;
  supplierQuickCreateForm: FormGroup;

  facilities: ContainerFacility[] = [];
  areas: ContainerYardArea[] = [];
  slots: ContainerYardSlot[] = [];
  chargeRules: ContainerYardChargeRule[] = [];
  locations: Location[] = [];
  supplierClients: CrmClient[] = [];
  warehouses: WarehousingMasterData[] = [];
  logisticsProducts: Products[] = [];

  readonly facilityTypes = [
    { label: 'Port Terminal', value: 'PORT_TERMINAL' },
    { label: 'Bonded Yard', value: 'BONDED_YARD' },
    { label: 'CFS / Transit Shed', value: 'CFS' },
    { label: 'Warehouse', value: 'WAREHOUSE' },
    { label: 'Empty Depot', value: 'EMPTY_DEPOT' },
    { label: 'Carrier Yard', value: 'CARRIER_YARD' },
    { label: 'Third Party Yard', value: 'THIRD_PARTY_YARD' }
  ];
  readonly operationModeOptions = [
    { label: 'Managed terminal / yard', value: 'MANAGED_YARD' },
    { label: 'Third-party reference only', value: 'REFERENCE_ONLY' }
  ];
  readonly areaTypes = [
    { label: 'Import Stack', value: 'IMPORT_STACK' },
    { label: 'Export Stack', value: 'EXPORT_STACK' },
    { label: 'Inspection', value: 'INSPECTION' },
    { label: 'Reefer', value: 'REEFER' },
    { label: 'Empty', value: 'EMPTY' },
    { label: 'Damaged', value: 'DAMAGED' },
    { label: 'Customs Hold', value: 'CUSTOMS_HOLD' }
  ];
  readonly chargeTypes = [
    { label: 'Storage', value: 'STORAGE' },
    { label: 'Demurrage', value: 'DEMURRAGE' },
    { label: 'Detention', value: 'DETENTION' },
    { label: 'Lift On', value: 'LIFT_ON' },
    { label: 'Lift Off', value: 'LIFT_OFF' },
    { label: 'Gate In', value: 'GATE_IN' },
    { label: 'Gate Out', value: 'GATE_OUT' },
    { label: 'Reefer Plug-in', value: 'REEFER_PLUGIN' },
    { label: 'Inspection', value: 'INSPECTION' },
    { label: 'Weighing', value: 'WEIGHING' },
    { label: 'Cleaning', value: 'CLEANING' },
    { label: 'Repair', value: 'REPAIR' }
  ];
  readonly chargeBasisOptions = [
    { label: 'Per Day', value: 'PER_DAY' },
    { label: 'Flat', value: 'FLAT' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private messageService: MessageService,
    private sessionStorageService: SessionStorageService,
    private crmService: CrmService,
    private locationService: LocationService,
    private warehousingService: WarehousingService,
    private facilityService: ContainerFacilityService,
    private yardService: ContainerTerminalYardService,
    private productsService: ProductsService,
    private dialogService: DialogService,
    @Optional() private dialogRef: DynamicDialogRef | null,
    @Optional() private dialogConfig: DynamicDialogConfig | null
  ) {
    this.supplierQuickCreateForm = this.fb.group({
      companyName: ['', Validators.required],
      primaryEmail: ['', Validators.email],
      phoneNumber: [''],
      natureOfBusiness: ['Terminal and yard operations']
    });
  }

  ngOnInit(): void {
    this.isDialogMode = !!this.dialogRef;
    if (this.isDialogMode) {
      this.master = (this.dialogConfig?.data?.master || 'facilities') as SetupMasterForm;
      this.id = Number(this.dialogConfig?.data?.id) || null;
      this.initForm();
      const defaults = this.dialogConfig?.data?.defaults;
      if (defaults) {
        this.form.patchValue(defaults, { emitEvent: false });
      }
      this.loadLookups();
      return;
    }
    this.route.data.subscribe(data => {
      this.master = (data['master'] || 'facilities') as SetupMasterForm;
      this.id = Number(this.route.snapshot.paramMap.get('id')) || null;
      this.initForm();
      this.loadLookups();
    });
  }

  get isEdit(): boolean {
    return !!this.id;
  }

  get title(): string {
    const prefix = this.isEdit ? 'Edit' : 'New';
    switch (this.master) {
      case 'areas': return `${prefix} Yard Area`;
      case 'slots': return `${prefix} Yard Slot`;
      case 'charge-rules': return `${prefix} Yard Charge Rule`;
      default: return `${prefix} Terminal Facility`;
    }
  }

  get subtitle(): string {
    switch (this.master) {
      case 'areas': return 'Maintain operational yard zone capabilities and facility linkage.';
      case 'slots': return 'Maintain block, row, bay, tier, slot capacity, and assignment availability.';
      case 'charge-rules': return 'Maintain estimated yard charge rules used by charge recalculation.';
      default: return 'Maintain terminal, depot, CFS, bonded yard, and carrier yard facility master data.';
    }
  }

  get listRoute(): string[] {
    return ['/container-tracking/setup', this.routeSegment];
  }

  get areaOptions(): ContainerYardArea[] {
    const facilityId = this.form?.get('facilityId')?.value;
    return facilityId ? this.areas.filter(area => area.facilityId === facilityId) : this.areas;
  }

  get linkedWarehouseOptions(): WarehousingMasterData[] {
    const locationId = this.form?.get('locationId')?.value;
    return locationId
      ? this.warehouses.filter(warehouse => !warehouse.locationId || warehouse.locationId === locationId)
      : this.warehouses;
  }

  openLocationQuickCreate(): void {
    this.locationQuickCreateVisible = true;
  }

  onLocationQuickCreated(result: PartyQuickCreateResult): void {
    if (result.type !== 'location') return;
    const location = result.record as Location;
    if (!location?.id) return;
    this.locations = this.upsertById(this.locations, location);
    this.form.patchValue({ locationId: location.id });
    this.locationQuickCreateVisible = false;
  }

  openSupplierQuickCreate(): void {
    this.supplierQuickCreateForm.reset({
      companyName: '',
      primaryEmail: '',
      phoneNumber: '',
      natureOfBusiness: 'Terminal and yard operations'
    });
    this.supplierQuickCreateVisible = true;
  }

  saveSupplierQuickCreate(): void {
    if (this.supplierQuickCreateForm.invalid || this.supplierQuickCreateSaving) {
      this.supplierQuickCreateForm.markAllAsTouched();
      return;
    }
    const value = this.supplierQuickCreateForm.getRawValue();
    const companyName = String(value.companyName || '').trim();
    const payload: CrmClient = {
      companyName,
      customerNameLocal: companyName,
      notifyingPersonName: companyName,
      postingDate: new Date(),
      currencyCode: 'SAR',
      exchangeRate: 1,
      businessType: 'B2B',
      natureOfBusiness: String(value.natureOfBusiness || '').trim(),
      primaryEmail: String(value.primaryEmail || '').trim(),
      phoneNumber: String(value.phoneNumber || '').trim(),
      category: 'STANDARD',
      website: '',
      source: 'TERMINAL_FACILITY_QUICK_CREATE',
      kamCustomerId: 0,
      roles: ['SUPPLIER'],
      companyId: this.companyId()
    };
    this.supplierQuickCreateSaving = true;
    this.crmService.addClient(payload).subscribe({
      next: response => {
        this.supplierQuickCreateSaving = false;
        const supplier = response.data;
        if (!response.success || !supplier?.id) {
          this.messageService.add({ severity: 'error', summary: 'Supplier create failed', detail: response.message || 'Could not create the CRM supplier.' });
          return;
        }
        this.supplierClients = this.upsertById(this.supplierClients, supplier);
        this.form.patchValue({ operatorCrmClientId: supplier.id });
        this.supplierQuickCreateVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Supplier created', detail: `${supplier.companyName} is selected as the terminal operator.` });
      },
      error: (error: any) => {
        this.supplierQuickCreateSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Supplier create failed', detail: error?.error?.message || 'Could not create the CRM supplier.' });
      }
    });
  }

  openWarehouseQuickCreate(): void {
    const ref = this.dialogService.open(WarehousingMasterDataFormComponent, {
      header: 'New Warehouse',
      modal: true,
      width: '94vw',
      breakpoints: { '1200px': '96vw', '760px': '100vw' },
      data: { type: 'WAREHOUSE' },
      styleClass: 'yard-setup-warehouse-dialog'
    });
    ref.onClose.subscribe((warehouse: WarehousingMasterData | undefined) => {
      if (!warehouse?.id) return;
      this.warehouses = this.upsertById(this.warehouses, warehouse);
      this.form.patchValue({ linkedWarehouseId: warehouse.id });
    });
  }

  openFacilityQuickCreate(): void {
    const ref = this.dialogService.open(YardSetupMasterFormComponent, {
      header: 'New Terminal Facility',
      modal: true,
      width: '88vw',
      breakpoints: { '1100px': '94vw', '760px': '100vw' },
      data: { master: 'facilities' },
      styleClass: 'yard-setup-master-dialog'
    });
    ref.onClose.subscribe((facility: ContainerFacility | undefined) => {
      if (!facility?.id) return;
      this.facilities = this.upsertById(this.facilities, facility);
      this.form.patchValue({ facilityId: facility.id });
    });
  }

  openAreaQuickCreate(): void {
    const facilityId = this.form.get('facilityId')?.value || null;
    const ref = this.dialogService.open(YardSetupMasterFormComponent, {
      header: 'New Yard Area',
      modal: true,
      width: '78vw',
      breakpoints: { '1000px': '92vw', '760px': '100vw' },
      data: { master: 'areas', defaults: { facilityId } },
      styleClass: 'yard-setup-master-dialog'
    });
    ref.onClose.subscribe((area: ContainerYardArea | undefined) => {
      if (!area?.id) return;
      this.areas = this.upsertById(this.areas, area);
      this.form.patchValue({ facilityId: area.facilityId, areaId: area.id });
    });
  }

  quickCreateFailed(message: string): void {
    if (!message) return;
    this.messageService.add({ severity: 'error', summary: 'Create failed', detail: message });
  }

  currentCompany(): Company | null {
    return this.sessionStorageService.getCompany() as Company | null;
  }

  countryOptions(): any[] {
    return this.sessionStorageService.getCountryList() || [];
  }

  defaultCountryId(): number | null {
    return this.currentCompany()?.countryId || null;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const companyId = this.companyId();
    const value = this.form.getRawValue();
    this.isSubmitting = true;
    let request: Observable<any>;
    if (this.master === 'facilities') {
      const payload: ContainerFacility = { ...value, owningCompanyId: companyId };
      request = this.id ? this.facilityService.update(this.id, payload) : this.facilityService.create(payload);
    } else if (this.master === 'areas') {
      const payload: ContainerYardArea = { ...value, owningCompanyId: companyId };
      request = this.id ? this.yardService.updateArea(companyId, this.id, payload) : this.yardService.createArea(companyId, payload);
    } else if (this.master === 'slots') {
      const payload: ContainerYardSlot = { ...value, owningCompanyId: companyId };
      request = this.id ? this.yardService.updateSlot(companyId, this.id, payload) : this.yardService.createSlot(companyId, payload);
    } else {
      const payload: ContainerYardChargeRule = { ...value, owningCompanyId: companyId };
      request = this.id ? this.yardService.updateChargeRule(companyId, this.id, payload) : this.yardService.createChargeRule(companyId, payload);
    }
    request.subscribe({
      next: saved => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: `${this.title} saved.` });
        if (this.isDialogMode && this.dialogRef) {
          this.dialogRef.close(saved);
          return;
        }
        this.router.navigate(this.listRoute);
      },
      error: (error: any) => {
        this.isSubmitting = false;
        this.messageService.add({ severity: 'error', summary: 'Save failed', detail: error?.error?.message || 'Could not save setup record.' });
      }
    });
  }

  cancel(): void {
    if (this.isDialogMode && this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    this.router.navigate(this.listRoute);
  }

  private initForm(): void {
    if (this.master === 'facilities') {
      this.form = this.fb.group({
        facilityType: ['PORT_TERMINAL', Validators.required],
        facilityCode: ['', Validators.required],
        facilityName: ['', Validators.required],
        locationId: [null],
        operatorCompanyId: [null],
        operatorCrmClientId: [null],
        operationMode: ['MANAGED_YARD', Validators.required],
        linkedWarehouseId: [null],
        operatingHours: [''],
        active: [true]
      });
      this.form.get('locationId')?.valueChanges.subscribe(() => {
        const warehouseId = this.form.get('linkedWarehouseId')?.value;
        if (warehouseId && !this.linkedWarehouseOptions.some(warehouse => warehouse.id === warehouseId)) {
          this.form.patchValue({ linkedWarehouseId: null }, { emitEvent: false });
        }
      });
      return;
    }
    if (this.master === 'areas') {
      this.form = this.fb.group({
        facilityId: [null, Validators.required],
        areaCode: ['', Validators.required],
        areaName: ['', Validators.required],
        areaType: ['IMPORT_STACK', Validators.required],
        temperatureControlled: [false],
        reeferSupported: [false],
        hazardousAllowed: [false],
        customsControlled: [false],
        active: [true]
      });
      return;
    }
    if (this.master === 'slots') {
      this.form = this.fb.group({
        facilityId: [null, Validators.required],
        areaId: [null, Validators.required],
        slotCode: ['', Validators.required],
        blockCode: [''],
        rowCode: [''],
        bayCode: [''],
        tierCode: [''],
        maxTeuCapacity: [1, [Validators.required, Validators.min(1)]],
        allowedContainerTypes: [''],
        active: [true]
      });
      this.form.get('facilityId')?.valueChanges.subscribe(() => {
        const areaId = this.form.get('areaId')?.value;
        if (areaId && !this.areaOptions.some(area => area.id === areaId)) {
          this.form.patchValue({ areaId: null }, { emitEvent: false });
        }
      });
      return;
    }
    this.form = this.fb.group({
      facilityId: [null, Validators.required],
      areaId: [null],
      containerType: [''],
      productId: [null],
      variantId: [null],
      chargeType: ['STORAGE', Validators.required],
      chargeBasis: ['PER_DAY', Validators.required],
      freeDays: [0],
      currencyCode: ['SAR', Validators.required],
      rateAmount: [0, Validators.required],
      active: [true]
    });
    this.form.get('productId')?.valueChanges.subscribe(productId => this.applyProductDefaults(productId));
  }

  private loadLookups(): void {
    const companyId = this.companyId();
    this.isLoading = true;
    const loadNonChargeRecord = () => {
      if (this.master !== 'charge-rules') {
        this.loadRecord();
      }
    };
    this.facilityService.findAll(new HttpParams().set('owningCompanyId', String(companyId)).set('size', '500')).subscribe({
      next: page => this.facilities = page.content || [],
      error: () => this.facilities = []
    });
    this.loadFacilityReferenceLookups();
    this.yardService.findAreas(new HttpParams().set('companyId', String(companyId))).subscribe({
      next: rows => this.areas = rows || [],
      error: () => this.areas = []
    });
    this.yardService.findSlots(new HttpParams().set('companyId', String(companyId))).subscribe({
      next: rows => {
        this.slots = rows || [];
        loadNonChargeRecord();
      },
      error: () => {
        this.slots = [];
        loadNonChargeRecord();
      }
    });
    this.yardService.findChargeRules(companyId).subscribe({
      next: rows => {
        this.chargeRules = rows || [];
        if (this.master === 'charge-rules') {
          this.loadRecord();
        }
      },
      error: () => {
        this.chargeRules = [];
        if (this.master === 'charge-rules') {
          this.loadRecord();
        }
      }
    });
    this.productsService.getLogisticsCatalog(companyId, 'CONTAINER_YARD').subscribe({
      next: products => this.logisticsProducts = products || [],
      error: () => this.logisticsProducts = []
    });
  }

  private loadFacilityReferenceLookups(): void {
    const company = this.sessionStorageService.getCompany() as Company | null;
    this.crmService.getClients(company?.id, ['SUPPLIER', 'WAREHOUSE_SUPPLIER', 'TRANSPORTER']).subscribe({
      next: rows => this.supplierClients = rows || [],
      error: () => this.supplierClients = []
    });
    this.warehousingService.getMasterData('WAREHOUSE', { activeStatus: 'ACTIVE' }).subscribe({
      next: response => this.warehouses = response.data || [],
      error: () => this.warehouses = []
    });
  }

  private loadRecord(): void {
    if (!this.id) {
      this.isLoading = false;
      return;
    }
    if (this.master === 'facilities') {
      this.facilityService.findById(this.id).subscribe({ next: record => this.patch(record), error: () => this.isLoading = false });
    } else if (this.master === 'areas') {
      this.patch(this.areas.find(area => area.id === this.id));
    } else if (this.master === 'slots') {
      this.patch(this.slots.find(slot => slot.id === this.id));
    } else {
      this.patch(this.chargeRules.find(rule => rule.id === this.id));
    }
  }

  private patch(record?: any): void {
    if (record) {
      this.form.patchValue(record, { emitEvent: false });
      if (this.master === 'facilities') {
        this.form.get('operationMode')?.updateValueAndValidity({ emitEvent: true });
      }
    }
    this.isLoading = false;
  }

  private companyId(): number {
    return this.sessionStorageService.getCompany()?.id || 0;
  }

  selectedProduct(): Products | undefined {
    return this.logisticsProducts.find(product => product.productID === this.form.get('productId')?.value);
  }

  productVariants(): ProductVariant[] {
    return this.selectedProduct()?.productVariants || [];
  }

  private applyProductDefaults(productId?: number | null): void {
    if (!productId) {
      this.form.patchValue({ variantId: null }, { emitEvent: false });
      return;
    }
    const product = this.logisticsProducts.find(item => item.productID === productId);
    if (!product) {
      return;
    }
    const usage = product.logisticsUsages?.find(item => item.usageModule === 'CONTAINER_YARD' && item.active !== false);
    const variants = product.productVariants || [];
    const variantId = usage?.defaultVariantId || (variants.length === 1 ? variants[0].variantID : null);
    const variant = variants.find(item => item.variantID === variantId);
    const rate = variant?.sellingRateAdjustment ?? product.sellingRate ?? product.standardRate ?? 0;
    this.form.patchValue({
      variantId,
      chargeType: usage?.operationalCode || this.form.get('chargeType')?.value,
      currencyCode: product.currencyCode || this.form.get('currencyCode')?.value,
      rateAmount: rate
    }, { emitEvent: false });
  }

  private upsertById<T extends { id?: number }>(rows: T[], record: T): T[] {
    return [...rows.filter(row => row.id !== record.id), record]
      .sort((left: any, right: any) => String(left.facilityName || left.areaName || left.name || left.companyName || '')
        .localeCompare(String(right.facilityName || right.areaName || right.name || right.companyName || '')));
  }

  private get routeSegment(): string {
    return this.master === 'slots' ? 'yard-slots' : this.master;
  }
}
